import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { useAuth } from './AuthProvider'
import { Icona, Marchio } from '../layout/Marchio'
import { FONT, PRIMARIO, PRIMARIO_SCURO } from '../tema'

const PUNTI = [
  'Dichiarazioni AD-1 calcolate, firmate e inviate alle Dogane',
  'Scadenze, registri e letture di tutti gli impianti in un posto',
  'Controllo consumi con avvisi sugli scostamenti',
]

const SPUNTA = 'm5 12.5 4.5 4.5L19 7.5'
const OCCHIO = 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12ZM12 14.8a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6Z'
const OCCHIO_CHIUSO = 'M3 3l18 18M10.6 5.6A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a16 16 0 0 1-2.9 3.7M6.6 6.6C3.9 8.4 2.5 12 2.5 12S6 18.5 12 18.5c1.9 0 3.5-.6 4.9-1.5M9.9 9.9a2.8 2.8 0 0 0 4.2 4.2'

export function Login() {
  const { accedi, reimpostaPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [visibile, setVisibile] = useState(false)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [inviato, setInviato] = useState(false)

  async function invia(e: FormEvent) {
    e.preventDefault()
    setErrore(null)
    setInCorso(true)
    try {
      await accedi(email, password)
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Accesso non riuscito')
    } finally {
      setInCorso(false)
    }
  }

  async function reimposta() {
    setErrore(null)
    if (!email) {
      setErrore('Scrivi la tua email, poi clicca di nuovo «Password dimenticata».')
      return
    }
    try {
      await reimpostaPassword(email)
      setInviato(true)
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'Invio non riuscito')
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      bgcolor: '#f8fafc', fontFamily: FONT, color: '#0f172a' }}>
      <Box component="aside" sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
        justifyContent: 'space-between', p: 6, color: '#fff',
        background: `linear-gradient(155deg, ${PRIMARIO} 0%, ${PRIMARIO_SCURO} 100%)` }}>
        <Marchio chiaro />
        <Box sx={{ maxWidth: 440 }}>
          <Typography component="p" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '2rem', lineHeight: 1.2, mb: 4 }}>
            Le pratiche energetiche dei tuoi impianti, senza sorprese.
          </Typography>
          <Stack component="ul" spacing={2} sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {PUNTI.map((p) => (
              <Box component="li" key={p} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', fontSize: '1rem', opacity: 0.92 }}>
                <Box sx={{ flexShrink: 0, mt: '2px', display: 'inline-flex', p: '3px', borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.18)' }}>
                  <Icona d={SPUNTA} dimensione={14} />
                </Box>
                {p}
              </Box>
            ))}
          </Stack>
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', opacity: 0.7 }}>© {new Date().getFullYear()} PraticheEnergia</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
        <Box component="form" onSubmit={invia}
          sx={{ width: '100%', maxWidth: 400, bgcolor: '#fff', borderRadius: '20px', p: { xs: 3, sm: 5 },
            border: '1px solid #e2e8f0', boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)' }}>
          <Box sx={{ display: { md: 'none' }, mb: 3 }}><Marchio /></Box>
          <Typography component="h1" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.75rem', mb: 0.5 }}>Accedi</Typography>
          <Typography sx={{ fontFamily: FONT, color: '#475569', mb: 3 }}>Entra nel portale con le credenziali del tuo account.</Typography>

          <Stack spacing={2.5}>
            <TextField label="Email" type="email" autoComplete="email" autoFocus required fullWidth
              value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField label="Password" type={visibile ? 'text' : 'password'} autoComplete="current-password" required fullWidth
              value={password} onChange={(e) => setPassword(e.target.value)}
              slotProps={{ input: { endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setVisibile((v) => !v)}
                    aria-label={visibile ? 'Nascondi i caratteri' : 'Mostra i caratteri'}>
                    <Icona d={visibile ? OCCHIO_CHIUSO : OCCHIO} />
                  </IconButton>
                </InputAdornment>
              ) } }} />

            {errore && <Alert severity="error" role="alert">{errore}</Alert>}
            {inviato && <Alert severity="success">Ti abbiamo inviato un messaggio per reimpostare la password.</Alert>}

            <Button type="submit" variant="contained" size="large" disabled={inCorso} disableElevation
              sx={{ fontFamily: FONT, fontWeight: 600, textTransform: 'none', fontSize: '1rem', borderRadius: '10px', py: 1.25,
                bgcolor: PRIMARIO, '&:hover': { bgcolor: PRIMARIO_SCURO } }}>
              {inCorso ? 'Accesso in corso…' : 'Accedi'}
            </Button>
            <Button type="button" onClick={reimposta}
              sx={{ fontFamily: FONT, textTransform: 'none', color: PRIMARIO, alignSelf: 'center' }}>
              Password dimenticata
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}
